import React from 'react';
import { Heart, Users, Target, Lightbulb } from 'lucide-react';

const About = () => {
  const team = [
    {
      name: 'Sarah Johnson',
      role: 'CEO & Founder',
      image: 'https://ui-avatars.com/api/?name=Sarah+Johnson&size=400&background=3b82f6&color=fff',
      description: 'Travel enthusiast with 15+ years in tech'
    },
    {
      name: 'Michael Chen',
      role: 'CTO',
      image: 'https://ui-avatars.com/api/?name=Michael+Chen&size=400&background=10b981&color=fff',
      description: 'Full-stack developer and adventure seeker'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Head of Design',
      image: 'https://ui-avatars.com/api/?name=Emily+Rodriguez&size=400&background=ec4899&color=fff',
      description: 'UX designer passionate about user experiences'
    },
    {
      name: 'David Kim',
      role: 'Product Manager',
      image: 'https://ui-avatars.com/api/?name=David+Kim&size=400&background=8b5cf6&color=fff',
      description: 'Product strategist and world traveler'
    }
  ];

  const values = [
    {
      icon: <Heart className="h-8 w-8 text-red-500" />,
      title: 'Passion for Travel',
      description: 'We believe travel enriches lives and creates lasting memories.'
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: 'Community First',
      description: 'Building connections between travelers worldwide.'
    },
    {
      icon: <Lightbulb className="h-8 w-8 text-yellow-500" />,
      title: 'Innovation',
      description: 'Constantly improving to make travel planning effortless.'
    },
    {
      icon: <Target className="h-8 w-8 text-green-500" />,
      title: 'User-Centric',
      description: 'Every feature is designed with our users in mind.'
    }
  ];

  return (
    <div className="min-h-screen py-20">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 text-center mb-20 animate-fade-in">
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
          About 
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> TripForge</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          We're on a mission to make travel planning collaborative, intuitive, and inspiring. 
          Born from our own travel frustrations, TripForge brings people together to create unforgettable journeys.
        </p>
      </div>

      {/* Story Section */}
      <div className="max-w-7xl mx-auto px-4 mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                TripForge was born in 2024 when our founders struggled to coordinate a group trip to Japan. 
                Endless email chains, conflicting preferences, and scattered planning documents made what should 
                have been exciting feel overwhelming.
              </p>
              <p>
                We realized there had to be a better way. A platform where friends and family could collaborate 
                in real-time, share ideas seamlessly, and turn travel dreams into detailed, actionable plans.
              </p>
              <p>
                Today, TripForge helps thousands of travelers worldwide plan better trips together. From weekend 
                getaways to month-long adventures, we're here to make every journey extraordinary.
              </p>
            </div>
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=400&fit=crop" 
                alt="Team planning" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="max-w-7xl mx-auto px-4 mb-20">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
          <p className="text-xl text-gray-600">The principles that guide everything we do</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <div 
              key={index}
              className="text-center p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex justify-center mb-4">
                {value.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
              <p className="text-gray-600">{value.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Team Section */}
      <div className="max-w-7xl mx-auto px-4 mb-20">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
          <p className="text-xl text-gray-600">The passionate people behind TripForge</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member, index) => (
            <div 
              key={index}
              className="text-center group animate-bounce-in"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="relative mb-4">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto shadow-lg group-hover:shadow-xl transform group-hover:scale-105 transition-all duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
              <p className="text-blue-600 font-medium mb-2">{member.role}</p>
              <p className="text-gray-600 text-sm">{member.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16 animate-fade-in">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-2">10K+</div>
              <div className="text-blue-100">Happy Travelers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">25K+</div>
              <div className="text-blue-100">Trips Planned</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">150+</div>
              <div className="text-blue-100">Countries Covered</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">99%</div>
              <div className="text-blue-100">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;